(() => {
  if (window.__msR16Motion) return;
  window.__msR16Motion = true;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');

  /* ── Effect 1 · Silk sheen ──────────────────────────────────────────────
     Writes pointer position into two custom properties. Nothing else: the
     gradient lives in CSS, so the only per-frame work is setting a string on
     a style object, which stays on the compositor. Reading offsetWidth here
     instead would force a layout on every pointermove. */
  const bindSheen = (scope = document) => {
    if (reduce.matches || !finePointer.matches) return;
    scope.querySelectorAll('.ms-sheen').forEach((el) => {
      if (el.dataset.msSheenBound) return;
      el.dataset.msSheenBound = '1';

      let raf = 0;
      let px = 0;
      let py = 0;
      let rect = null;

      const paint = () => {
        raf = 0;
        el.style.setProperty('--ms-mx', px + '%');
        el.style.setProperty('--ms-my', py + '%');
      };

      // The rect is measured once on entry, not on every move. A
      // getBoundingClientRect() inside pointermove is a layout read running up
      // to 120x a second per card, and on a throttled CPU that is exactly the
      // kind of thing that turns a smooth effect into jank. The card cannot
      // move while the pointer is inside it without a scroll or resize, and
      // both of those invalidate the cache.
      const measure = () => { rect = el.getBoundingClientRect(); };
      const invalidate = () => { rect = null; };

      el.addEventListener('pointerenter', measure, { passive: true });
      addEventListener('scroll', invalidate, { passive: true });
      addEventListener('resize', invalidate, { passive: true });

      el.addEventListener('pointermove', (e) => {
        if (!rect) measure();
        px = ((e.clientX - rect.left) / rect.width) * 100;
        py = ((e.clientY - rect.top) / rect.height) * 100;
        if (!raf) raf = requestAnimationFrame(paint);
      }, { passive: true });

      el.addEventListener('pointerleave', () => {
        // ease back to centre so the next hover does not jump
        el.style.setProperty('--ms-mx', '50%');
        el.style.setProperty('--ms-my', '50%');
      }, { passive: true });
    });
  };

  /* ── Effect 2 · The thread ──────────────────────────────────────────────
     Two paths. Where the browser has scroll-driven animations the CSS does
     all of it and no JS runs at all. Everywhere else -- Safari included, and
     an export site cannot treat Safari as an edge case -- this drives the
     same custom property from a scroll listener. */
  const nativeScrollTimeline = CSS.supports('animation-timeline: view()');

  const bindThread = (scope = document) => {
    const rails = [...scope.querySelectorAll('[data-ms-thread]')];
    if (!rails.length) return;

    if (reduce.matches) {
      rails.forEach((r) => r.style.setProperty('--thread-progress', '1'));
      return;
    }

    if (nativeScrollTimeline) {
      rails.forEach((r) => r.classList.add('ms-thread-native'));
      return;
    }

    let raf = 0;
    const update = () => {
      raf = 0;
      rails.forEach((rail) => {
        const r = rail.getBoundingClientRect();
        // 0 when the top edge reaches the lower third, 1 when the element has
        // travelled its own height past that point
        const start = innerHeight * 0.85;
        const p = (start - r.top) / (r.height + start * 0.15);
        rail.style.setProperty('--thread-progress', Math.max(0, Math.min(1, p)).toFixed(3));
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };

    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    update();
  };

  /* ── Effect 3 · Counted numbers ─────────────────────────────────────────
     The values on this site are not plain integers -- "80+", "4M metres",
     "4,000 m²" -- so the number has to be lifted out, animated, and put back
     between its own prefix and suffix. Getting this wrong destroys the copy.

     A year is a label, not a quantity: counting 0 -> 2002 means nothing and
     reads as a cheap trick, so years are skipped entirely. */
  const parseValue = (raw) => {
    const m = raw.match(/^([^\d]*)([\d][\d,.]*)(.*)$/s);
    if (!m) return null;
    const digits = m[2];
    const isYear = /^(19|20)\d{2}$/.test(digits) && m[3].trim() === '';
    if (isYear) return null;
    return {
      prefix: m[1],
      suffix: m[3],
      target: parseFloat(digits.replace(/,/g, '')),
      grouped: digits.includes(','),
      decimals: (digits.split('.')[1] || '').length
    };
  };

  const render = (el, v, spec) => {
    const n = spec.decimals ? v.toFixed(spec.decimals) : Math.round(v);
    const shown = spec.grouped ? Number(n).toLocaleString('en-US') : n;
    el.textContent = spec.prefix + shown + spec.suffix;
  };

  // A cubic ease-out over rAF. anime.js was carried here for exactly this
  // curve and removed in R17: it cost 75 KB decoded to duplicate the eight
  // lines below, which were measured producing identical output and an
  // identical final value.
  const countUp = (el, spec) => {
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 900);
      render(el, spec.target * (1 - Math.pow(1 - p, 3)), spec);
      if (p < 1) requestAnimationFrame(step);
      else render(el, spec.target, spec);
    };
    requestAnimationFrame(step);
  };

  const bindCounters = (scope = document) => {
    const els = [...scope.querySelectorAll('[data-ms-count]')].filter((el) => !el.dataset.msCountDone);
    if (!els.length) return;

    els.forEach((el) => {
      el.dataset.msCountDone = '1';
      const raw = el.textContent.trim();
      const spec = parseValue(raw);
      if (!spec) return;                       // year, or nothing numeric
      if (reduce.matches) return;              // leave the final value in place
      el.dataset.msCountRaw = raw;

      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          countUp(entry.target, spec);
        });
      }, { threshold: 0.5 });
      io.observe(el);
    });
  };

  /* Staggered entrance for evidence groups. */
  const bindStagger = (scope = document) => {
    const groups = [...scope.querySelectorAll('[data-ms-stagger]')].filter((g) => !g.dataset.msStaggerBound);
    groups.forEach((group) => {
      group.dataset.msStaggerBound = '1';
      const items = [...group.children];
      if (!items.length) return;

      if (reduce.matches) { items.forEach((i) => i.classList.add('is-in')); return; }

      items.forEach((i) => i.classList.add('ms-stagger-item'));
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          items.forEach((item, i) => setTimeout(() => item.classList.add('is-in'), i * 60));
        });
      }, { threshold: 0.15 });
      io.observe(group);

      // Same safety rule as the R14 reveal: if the observer never runs, show
      // everything. Content must never be lost to a failed animation.
      setTimeout(() => {
        if (items.some((i) => !i.classList.contains('is-in'))) {
          const r = group.getBoundingClientRect();
          if (r.top < innerHeight && r.bottom > 0) items.forEach((i) => i.classList.add('is-in'));
        }
      }, 2500);
    });
  };

  const run = (scope = document) => {
    bindSheen(scope);
    bindThread(scope);
    bindCounters(scope);
    bindStagger(scope);
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => run(), { once: true })
    : run();

  document.addEventListener('shopify:section:load', (e) => run(e.target));
})();
